import type { LucideIcon } from "lucide-react";

import { Dumbbell, Flame, ICON_STROKE } from "@/shared/components/icons";
import type { SkillState } from "@/shared/models";
import { ClickableSkillRow } from "@/features/skills/components/SkillRow";

/** "Sharpest" + "Currently working on" — the top/bottom 3 mapped skills. */
export function SummaryStrip({ skills }: { skills: SkillState[] }) {
  if (skills.length === 0) return null;
  const sorted = [...skills].sort((a, b) => b.level - a.level);
  const sharpest = sorted.slice(0, 3);
  const workingOn = [...sorted].reverse().slice(0, 3);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <SummaryBlock Icon={Flame} title="Sharpest" tint="amber" skills={sharpest} />
      <SummaryBlock Icon={Dumbbell} title="Currently working on" tint="accent" skills={workingOn} />
    </div>
  );
}

function SummaryBlock({
  Icon,
  title,
  tint,
  skills,
}: {
  Icon: LucideIcon;
  title: string;
  tint: "amber" | "accent";
  skills: SkillState[];
}) {
  const iconBg = tint === "amber" ? "bg-amber-500/15 text-amber-400" : "bg-accent/15 text-accent";
  return (
    <section className="rounded-xl border border-border bg-surface p-4 space-y-3">
      <div className="flex items-center gap-2.5 text-sm font-semibold tracking-tight">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconBg}`}>
          <Icon size={15} strokeWidth={ICON_STROKE} />
        </span>
        <span>{title}</span>
      </div>
      <div className="space-y-2">
        {skills.map((s) => (
          <ClickableSkillRow key={s.skillId} skill={s} compact />
        ))}
      </div>
    </section>
  );
}
